import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { AnalyticsInterval } from "@/redux/certifications/types";
import {
  useGetCertificationStatsQuery,
  useGetCommunityAnalyticsQuery,
  useGetKnowledgeCheckAnalyticsQuery,
  useGetModulesAnalyticsQuery,
  useGetProgressionAnalyticsQuery,
  useGetTimelineAnalyticsQuery,
  useGetTracksAnalyticsQuery,
} from "@/redux/service";
import AnalyticsFilters from "@/features/certifications/analytics/components/AnalyticsFilters";
import AnalyticsSections from "@/features/certifications/analytics/components/AnalyticsSections";
import CommunityTopicsCard from "@/features/certifications/analytics/components/CommunityTopicsCard";
import EnrollmentSummaryCard from "@/features/certifications/analytics/components/EnrollmentSummaryCard";
import KnowledgeCheckTableCard from "@/features/certifications/analytics/components/KnowledgeCheckTableCard";
import ModuleContentCard from "@/features/certifications/analytics/components/ModuleContentCard";
import ParticipantFunnelCard from "@/features/certifications/analytics/components/ParticipantFunnelCard";
import ParticipantStatusCard from "@/features/certifications/analytics/components/ParticipantStatusCard";
import PathSplitCard from "@/features/certifications/analytics/components/PathSplitCard";
import ProgressionCard from "@/features/certifications/analytics/components/ProgressionCard";
import ReviewQueueCard from "@/features/certifications/analytics/components/ReviewQueueCard";
import TimelineCards from "@/features/certifications/analytics/components/TimelineCards";
import TrackComparisonCard from "@/features/certifications/analytics/components/TrackComparisonCard";
import {
  ANALYTICS_MAX_POINTS,
  DEFAULT_FILTERS,
  SECTION_KEYS,
} from "@/features/certifications/analytics/constants";
import {
  countBuckets,
  getCardState,
  getProgressionInterval,
} from "@/features/certifications/analytics/utils";
import {
  AnalyticsFilterState,
  AnalyticsTabProps,
} from "@/features/certifications/analytics/types";

import "./styles.css";

const AnalyticsTab = ({ certification }: AnalyticsTabProps) => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<AnalyticsFilterState>(DEFAULT_FILTERS);
  const prefix = "certification.admin.analytics";

  const certificationSlug = certification.slug;
  const { track, interval } = filters;
  const getPeriod = (bucketInterval: AnalyticsInterval) =>
    countBuckets({ ...filters, interval: bucketInterval }) >
    ANALYTICS_MAX_POINTS
      ? { interval: bucketInterval }
      : {
          startDate: filters.startDate,
          endDate: filters.endDate,
          interval: bucketInterval,
        };
  const period = getPeriod(interval);
  const isPeriodTooLong = period.startDate === undefined && !!filters.startDate;
  const progressionInterval = getProgressionInterval(interval);
  const progressionPeriod = getPeriod(progressionInterval);

  const stats = useGetCertificationStatsQuery(certificationSlug);
  const timeline = useGetTimelineAnalyticsQuery({
    certificationSlug,
    track,
    ...period,
  });
  const progression = useGetProgressionAnalyticsQuery({
    certificationSlug,
    track,
    ...progressionPeriod,
  });
  const modules = useGetModulesAnalyticsQuery({ certificationSlug, track });
  const knowledgeChecks = useGetKnowledgeCheckAnalyticsQuery({
    certificationSlug,
    track,
  });
  const community = useGetCommunityAnalyticsQuery({ certificationSlug, track });
  const tracks = useGetTracksAnalyticsQuery({ certificationSlug });

  if (stats.data?.enrollments.total === 0) {
    return (
      <p className="analytics-card analytics-muted flex justify-center body-m-regular">
        {t(`${prefix}.empty`)}
      </p>
    );
  }

  return (
    <div className="analytics-tab flex flex-col gap-500">
      <AnalyticsFilters
        certification={certification}
        filters={filters}
        onChange={setFilters}
        periodError={
          isPeriodTooLong
            ? t(`${prefix}.filters.tooManyPoints`, {
                max: ANALYTICS_MAX_POINTS,
              })
            : null
        }
      />

      <AnalyticsSections
        sections={[
          {
            key: SECTION_KEYS.OVERVIEW,
            title: t(`${prefix}.sections.overview`),
            scope: t(`${prefix}.scope.overview`),
            children: (
              <>
                <EnrollmentSummaryCard {...getCardState(stats)} />
                <ParticipantStatusCard {...getCardState(stats)} />
                <ReviewQueueCard {...getCardState(stats)} />
              </>
            ),
          },
          {
            key: SECTION_KEYS.ACTIVITY,
            title: t(`${prefix}.sections.activity`),
            scope: t(`${prefix}.scope.filtered`),
            children: (
              <>
                <ProgressionCard
                  {...getCardState(progression)}
                  isIntervalAdjusted={progressionInterval !== interval}
                />
                <TimelineCards {...getCardState(timeline)} />
              </>
            ),
          },
          {
            key: SECTION_KEYS.WEEKS,
            title: t(`${prefix}.sections.weeks`),
            scope: t(`${prefix}.scope.allTimeByPath`),
            children: (
              <>
                <ParticipantFunnelCard {...getCardState(modules)} />
                <ModuleContentCard {...getCardState(modules)} />
              </>
            ),
          },
          {
            key: SECTION_KEYS.KNOWLEDGE_CHECKS,
            title: t(`${prefix}.sections.knowledgeChecks`),
            scope: t(`${prefix}.scope.allTimeByPath`),
            children: (
              <KnowledgeCheckTableCard
                certification={certification}
                {...getCardState(knowledgeChecks)}
              />
            ),
          },
          {
            key: SECTION_KEYS.COMMUNITY,
            title: t(`${prefix}.sections.community`),
            scope: t(`${prefix}.scope.allTimeByPath`),
            children: (
              <CommunityTopicsCard
                certification={certification}
                {...getCardState(community)}
              />
            ),
          },
          {
            key: SECTION_KEYS.PATHS,
            title: t(`${prefix}.sections.paths`),
            scope: t(`${prefix}.scope.paths`),
            children: (
              <>
                <PathSplitCard
                  certification={certification}
                  {...getCardState(tracks)}
                />
                <TrackComparisonCard
                  certification={certification}
                  stats={stats.data}
                  {...getCardState(tracks)}
                />
              </>
            ),
          },
        ]}
      />
    </div>
  );
};

export default AnalyticsTab;
